import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const PRODUCT_NAME = 'FIBER OPTIC DIAGNOSTIC PRO™'
export const CHECKOUT_URL = 'https://fiber-optic-diagnostic-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1118768284039792'

export const BOX_IMAGE = 'https://cdn.magicpatterns.com/uploads/9JH9rJCZzLSh1Dej78U1Bp/Diseno-sin-titulo-18.webp'

export const diagnosticFlow = ['IDENTIFY', 'MEASURE', 'ANALYZE', 'ISOLATE', 'REPAIR', 'VERIFY']

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/4KQsHdfv5vWybHGyMWgPrf/Captura-de-pantalla-2026-10-10-a-la_s_-2.18.26_p._m..png',
    alt: 'Macro-Bending Signatures & Verification — 1310 nm vs 1550 nm traces, enclosure inspection and structured diagnostic workflow',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3oA2G5aUsbey3ZYjVkf92x/Captura-de-pantalla-2026-10-10-a-la_s_-2.18.03_p._m..png',
    alt: 'Reflective Event Signatures — four annotated OTDR traces with signature feature and verification table',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/epTX58V9XBBVCtL7T9xs1d/Captura-de-pantalla-2026-10-10-a-la_s_-2.18.41_p._m..png',
    alt: 'Fault Distance vs. Physical Cable Location — route map and fault-location correlation worksheet',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/cGn25fvj5APuokSzhd2251/Captura-de-pantalla-2026-10-10-a-la_s_-2.18.13_p._m..png',
    alt: 'Bidirectional OTDR Testing Methodology — A→B and B→A traces with structured comparison worksheet',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/r7W84Eebxqeiziv37djs5n/Captura-de-pantalla-2026-10-10-a-la_s_-2.17.51_p._m..png',
    alt: 'Pulse Width Selection & Event Resolution — short, medium and long pulse traces with selection guide',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 TOO MANY POSSIBLE CAUSES. TOO MUCH TIME TROUBLESHOOTING.',
    body: 'Unexpected attenuation? Unstable optical power? Confusing OTDR events? Intermittent connectivity? Without a structured diagnostic process, locating the fault can turn into repeated measurements, unnecessary inspections, and wasted field time.',
  },
  {
    title: '⚠️ YOU FOUND THE SIGNAL PROBLEM—BUT WHERE IS THE ACTUAL FAULT?',
    body: "A high-loss event doesn't automatically identify a damaged splice. An OTDR reflection doesn't always indicate the expected physical fault. Without proper trace interpretation and fault isolation, technicians can investigate the wrong network segment.",
  },
  {
    title: '📚 YOUR TECHNICAL INFORMATION IS EVERYWHERE',
    body: "OTDR traces, equipment manuals, optical power measurements, network diagrams, splice records, inspection notes, and troubleshooting procedures. The information exists—but it's scattered when you need to make a diagnostic decision.",
  },
  {
    title: '😩 STOP APPROACHING EVERY FIBER FAILURE FROM SCRATCH',
    body: 'Moving between possible causes without a clear diagnostic sequence creates unnecessary work. Approach each fault with organized measurements, trace interpretation, network segmentation, and repair verification.',
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'ADVANCED FIBER OPTIC FAULT DIAGNOSIS & NETWORK FAILURE ANALYSIS™',
    body: 'Investigate complex optical network failures, intermittent signal degradation, abnormal attenuation, reflective events, and multi-point faults with professional diagnostic matrices, network analysis references, and structured fault-isolation procedures.',
  },
  {
    number: 2,
    title: 'PROFESSIONAL OTDR TRACE ANALYSIS & FAULT LOCATION™',
    body: 'Interpret reflective and non-reflective events, splice losses, ghost events, dead zones, bidirectional measurements, macro-bending signatures, and complex trace anomalies using annotated references and advanced fault-location workflows.',
  },
  {
    number: 3,
    title: 'OPTICAL POWER, INSERTION LOSS & NETWORK PERFORMANCE DIAGNOSTICS™',
    body: 'Evaluate optical power levels, insertion loss, attenuation, return loss, splitter performance, and network signal degradation using measurement worksheets, optical budget references, and professional diagnostic procedures.',
  },
  {
    number: 4,
    title: 'FIBER OPTIC CONNECTOR, SPLICE & PHYSICAL LINK FAILURE ANALYSIS™',
    body: 'Identify connector contamination, damaged end faces, defective splices, cable stress, fiber bends, closure problems, and physical link degradation with inspection matrices, technical references, and corrective-action workflows.',
  },
  {
    number: 5,
    title: 'ADVANCED FTTH, PON & TELECOMMUNICATION NETWORK TROUBLESHOOTING™',
    body: 'Troubleshoot GPON, XGS-PON, FTTH distribution networks, optical splitters, OLT/ONT connections, upstream and downstream optical power anomalies, and complex service failures with network diagrams and structured diagnostic procedures.',
  },
  {
    number: 6,
    title: 'REAL-WORLD FIBER OPTIC FAULT CASES & PROFESSIONAL DIAGNOSTIC WORKFLOWS™',
    body: 'Work through complex field scenarios involving intermittent faults, misleading OTDR events, unexplained attenuation, damaged distribution cables, defective connections, and multi-fault networks using diagnostic decision trees and verification checklists.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'FIBER OPTIC FIELD DIAGNOSTIC CHECKLISTS & INSPECTION TOOLKIT™',
    body: 'Organize field inspections with professional diagnostic checklists, connector inspection records, equipment verification forms, network assessment worksheets, and post-intervention inspection tools.',
  },
  {
    number: 2,
    title: 'OTDR TRACE INTERPRETATION & EVENT ANALYSIS WORKBOOK™',
    body: 'Practice advanced OTDR interpretation with realistic trace scenarios, event classification exercises, bidirectional measurement comparisons, diagnostic worksheets, and detailed answer references.',
  },
  {
    number: 3,
    title: 'OPTICAL LOSS BUDGET & POWER MEASUREMENT TOOLKIT™',
    body: 'Evaluate optical performance with loss-budget calculation worksheets, power measurement records, splitter loss references, engineering margin assessments, and practical calculation examples.',
  },
  {
    number: 4,
    title: 'FIBER OPTIC FAULT ISOLATION & ROOT CAUSE ANALYSIS PLAYBOOK™',
    body: 'Investigate difficult network faults with symptom-to-cause matrices, structured isolation procedures, competing-hypothesis worksheets, root cause analysis tools, and professional troubleshooting cases.',
  },
  {
    number: 5,
    title: 'FIBER OPTIC REPAIR VERIFICATION & TECHNICAL REPORTING PACK™',
    body: 'Document completed repairs with post-repair testing checklists, optical measurement records, OTDR comparison forms, corrective-action reports, and professional technical handover templates.',
  },
]

export const differentiators: Differentiator[] = [
  {
    emoji: '🔬',
    title: 'Advanced diagnostic references',
    body: 'Access OTDR interpretation guides, optical measurement tools, fault-isolation matrices, and professional troubleshooting procedures.',
  },
  {
    emoji: '🔍',
    title: 'Structured fault-finding process',
    body: 'Move from identifying abnormal measurements to analyzing evidence, isolating possible faults, planning corrective actions, and verifying results.',
  },
  {
    emoji: '⚙️',
    title: '6 specialized diagnostic areas',
    body: 'Network failures, OTDR analysis, optical performance, physical link defects, FTTH/PON troubleshooting, and real-world fault cases.',
  },
  {
    emoji: '🎁',
    title: '5 practical professional bonuses',
    body: 'Field inspection checklists, OTDR exercises, optical loss calculations, root cause analysis tools, and technical reporting templates.',
  },
  {
    emoji: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever you need to interpret test results, investigate network failures, document repairs, or verify optical performance.',
  },
]

export const offerBullets = [
  '6 advanced fiber optic diagnostic modules',
  '5 strategic professional bonuses',
  'Advanced OTDR trace interpretation references',
  'Optical power and insertion loss diagnostic tools',
  'Connector, splice, and physical link troubleshooting',
  'FTTH, GPON, and XGS-PON fault analysis',
  'Real-world diagnostic cases and decision trees',
  'Field inspection and repair verification templates',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Daniel Harris, 38',
    role: 'Fiber Optic Field Technician',
    location: 'Denver, United States',
    headline: 'OTDR troubleshooting feels much more organized.',
    quote:
      'Instead of jumping between different possible events, I can follow a structured process to interpret the trace, evaluate the measurements, and decide what needs to be tested next.',
  },
  {
    name: 'Sophie Turner, 34',
    role: 'Fiber Optic Testing Technician',
    location: 'Manchester, United Kingdom',
    headline: 'The OTDR interpretation resources are extremely practical.',
    quote:
      'Complex traces can be difficult to evaluate when several events appear close together. Having structured references and comparison tools makes the investigation easier to organize.',
  },
  {
    name: 'Michael Chen, 45',
    role: 'Telecommunications Maintenance Technician',
    location: 'Toronto, Canada',
    headline: 'Much better than scattered technical references.',
    quote:
      'I regularly work with OTDR results, network diagrams, equipment specifications, and inspection records. Having these diagnostic resources organized in one system is very useful.',
  },
  {
    name: 'Rebecca Lawson, 41',
    role: 'Optical Network Technician',
    location: 'Brisbane, Australia',
    headline: 'The fault-isolation workflows are a valuable resource.',
    quote:
      'Finding an abnormal measurement is only the beginning. The structured workflows help organize the next tests, evaluate possible causes, and document the technical findings.',
  },
  {
    name: 'Andrew Collins, 52',
    role: 'Fiber Optic Network Specialist',
    location: 'Austin, United States',
    headline: 'A professional reference I can keep using.',
    quote:
      'The inspection checklists, loss-budget worksheets, OTDR exercises, and repair verification forms are practical resources to return to during different field assignments.',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Experienced fiber optic technicians, telecommunications maintenance professionals, optical network specialists, FTTH installers, testing technicians, and qualified professionals responsible for diagnosing fiber optic infrastructure.',
  },
  {
    q: 'What types of fiber optic faults does it cover?',
    a: 'The system addresses excessive attenuation, abnormal reflections, connector defects, splice losses, macro-bending, optical power problems, intermittent link failures, and FTTH/PON distribution faults.',
  },
  {
    q: 'Does it include advanced OTDR interpretation?',
    a: 'Yes. The system includes OTDR event analysis, reflective and non-reflective event references, ghost events, dead zones, bidirectional testing, trace comparisons, and professional fault-location workflows.',
  },
  {
    q: 'Does it include ready-to-use diagnostic resources?',
    a: 'Yes. Diagnostic checklists, optical loss worksheets, fault-isolation decision trees, measurement records, inspection forms, and technical reporting templates are included.',
  },
  {
    q: 'Is this suitable for beginners?',
    a: 'The system is designed primarily for technicians with existing fiber optic knowledge and field experience. It focuses on advanced diagnostics rather than introductory training.',
  },
  {
    q: 'Does it cover FTTH and PON networks?',
    a: 'Yes. It includes professional troubleshooting references for FTTH distribution, GPON, XGS-PON, optical splitters, OLT/ONT connections, and related optical network problems.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. FIBER OPTIC DIAGNOSTIC PRO™ is a 100% digital professional resource.',
  },
  {
    q: 'Is this a certification or training course?',
    a: 'No. It is a professional diagnostic and reference system intended to support experienced technicians during optical network troubleshooting.',
  },
  {
    q: 'Does it replace professional testing equipment?',
    a: 'No. The system supports technical decision-making and documentation. Appropriate testing instruments, manufacturer specifications, safety procedures, and professional judgment remain essential.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'Daniel H.', city: 'Denver', country: 'United States' },
  { name: 'Sophie T.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Michael C.', city: 'Toronto', country: 'Canada' },
  { name: 'Rebecca L.', city: 'Brisbane', country: 'Australia' },
  { name: 'Kevin R.', city: 'Phoenix', country: 'United States' },
  { name: 'Oliver H.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Ethan P.', city: 'Calgary', country: 'Canada' },
  { name: 'Sarah W.', city: 'Perth', country: 'Australia' },
  { name: 'Derek S.', city: 'Dallas', country: 'United States' },
  { name: 'James K.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Tyler N.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ben L.', city: 'Melbourne', country: 'Australia' },
]
